import React, { useState } from "react";
import { FiAlertTriangle, FiTrash2 } from "react-icons/fi";
import { getUserDetailsCache } from "../../../utils/authHelpers";
import { Link } from "react-router-dom";

interface FeedbackOption {
  id: string;
  text: string;
}

export interface UserProfileDetails {
  _id: string;
  name?: string;
  email: string;
  role: "user" | "admin" | "super_admin" | undefined;
  subscriptionPlan: string;
  isEmailVerified: boolean;
  provider: string;
  reports: any[] | undefined;
  createdAt: string;
}

const ProfileDeletionRequest = () => {
  const [step, setStep] = useState(1);
  const [confirmed, setConfirmed] = useState(false);
  const [deletionConfirm, setDeletionConfirm] = useState("");
  const [selectedReason, setSelectedReason] = useState("");
  const [additionalFeedback, setAdditionalFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const user = getUserDetailsCache() as UserProfileDetails;

  const feedbackOptions: FeedbackOption[] = [
    { id: "not-useful", text: "The service is not useful to me" },
    { id: "found-alternative", text: "I found a better alternative" },
    { id: "too-complex", text: "The platform is too complex to use" },
    { id: "privacy-concerns", text: "I have privacy concerns" },
    { id: "too-expensive", text: "The service is too expensive" },
    { id: "poor-customer-service", text: "Poor customer service" },
    { id: "temporary-break", text: "I need a temporary break" },
    { id: "other", text: "Other reason" },
  ];

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleNext = () => {
    if (step < 3) {
      setStep(step + 1);
    }
  };

  const handleSubmit = async () => {
    // Validate the form
    if (!confirmed) {
      setError(
        "You must confirm that you understand the consequences of account deletion"
      );
      return;
    }

    if (deletionConfirm !== "UNSPOKENN") {
      setError('Please type "UNSPOKENN" to confirm');
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      // Mock API call - replace with actual API request
      await new Promise((resolve) => setTimeout(resolve, 1500));
      // If successful
      setSuccess(true);
    } catch (err) {
      setError("Failed to submit deletion request. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiTrash2 className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Deletion Request Received
          </h2>
          <p className="text-gray-600 mb-6">
            We've received your request to delete your account.
            {user?.email && (
              <span>
                {" "}
                We'll process your request and send a confirmation email to{" "}
                {user.email} within 24 hours.
              </span>
            )}
            {!user?.email && (
              <span> We'll process your request within 24 hours.</span>
            )}
          </p>
          <p className="text-gray-600 mb-6">
            Your account will be scheduled for deletion, but you can still log
            in and cancel this request for the next 14 days.
          </p>
          <Link
            to="/profile/settings"
            className="px-6 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Return to Settings
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiTrash2 className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">
            Request Account Deletion
          </h1>
          <p className="text-gray-600 mt-2">
            We're sorry to see you go. Please follow the steps below to request
            deletion of your account.
          </p>
        </div>

        <div className="mb-8">
          <div className="flex items-center mb-6">
            {[1, 2, 3].map((stepNumber) => (
              <React.Fragment key={stepNumber}>
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center font-medium text-sm ${
                    step >= stepNumber
                      ? "bg-gray-7 text-white"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {stepNumber}
                </div>
                {stepNumber < 3 && (
                  <div
                    className={`h-1 flex-1 ${
                      step > stepNumber ? "bg-gray-7" : "bg-gray-200"
                    }`}
                  ></div>
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 font-medium">
              Understand Implications
            </span>
            <span
              className={`${
                step >= 2 ? "text-gray-600" : "text-gray-400"
              } font-medium`}
            >
              Provide Feedback
            </span>
            <span
              className={`${
                step >= 3 ? "text-gray-600" : "text-gray-400"
              } font-medium`}
            >
              Confirm Deletion
            </span>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6 flex items-start">
            <FiAlertTriangle className="h-5 w-5 mr-2 mt-0.5" />
            <p>{error}</p>
          </div>
        )}

        {step === 1 && (
          <div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex">
                <FiAlertTriangle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
                <div className="text-yellow-700">
                  <h3 className="font-semibold">Important Information</h3>
                  <p className="mt-1">
                    Deleting your account is permanent and cannot be undone.
                    Please review the following information carefully.
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="font-medium text-gray-800 mb-3">
                What will happen when your account is deleted:
              </h3>
              <ul className="list-disc pl-5 space-y-2 text-gray-600">
                <li>
                  All your personal information will be permanently deleted
                </li>
                <li>Your profile will no longer be visible to other users</li>
                <li>All your stored data and reports will be removed</li>
                <li>
                  You will lose access to any purchased products or services
                </li>
                <li>Your subscription plan will be canceled</li>
                {user?.subscriptionPlan && user.subscriptionPlan !== "free" && (
                  <li>
                    Your subscription to the {user.subscriptionPlan} plan will
                    be terminated
                  </li>
                )}
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="font-medium text-gray-800 mb-3">
                What will be retained:
              </h3>
              <ul className="list-disc pl-5 space-y-2 text-gray-600">
                <li>Information required for legal and compliance purposes</li>
                <li>Anonymized usage data for analytics</li>
                <li>
                  Copies of data in our backups (these will expire according to
                  our retention policy)
                </li>
              </ul>
            </div>

            <div className="mb-6">
              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  className="h-5 w-5 rounded border-gray-3 bg-gray-7 text-gray-7 focus:ring-gray-4 mt-0.5"
                  checked={confirmed}
                  onChange={() => setConfirmed(!confirmed)}
                />
                <span className="ml-2 text-gray-700">
                  I understand that deleting my account is permanent and cannot
                  be undone after the 14-day cancellation period.
                </span>
              </label>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <div className="mb-6">
              <h3 className="font-medium text-gray-800 mb-3">
                We value your feedback
              </h3>
              <p className="text-gray-600 mb-4">
                We're constantly working to improve our service. Could you
                please let us know why you're leaving?
              </p>

              <div className="space-y-3">
                {feedbackOptions.map((option) => (
                  <label
                    key={option.id}
                    className="flex items-center cursor-pointer"
                  >
                    <input
                      type="radio"
                      className="h-4 w-4 text-gray-5 focus:ring-gray-5"
                      name="feedback"
                      value={option.id}
                      checked={selectedReason === option.id}
                      onChange={() => setSelectedReason(option.id)}
                    />
                    <span className="ml-2 text-gray-700">{option.text}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-gray-700 font-medium mb-2">
                Additional comments (optional)
              </label>
              <textarea
                className="block w-full border border-gray-2 p-2 rounded-[10px] shadow-sm focus:ring-gray-5 focus:border-gray-5 outline-none"
                rows={4}
                value={additionalFeedback}
                onChange={(e) => setAdditionalFeedback(e.target.value)}
                placeholder="Please share any additional feedback that could help us improve..."
              ></textarea>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <div className="mb-6">
              <h3 className="font-medium text-gray-800 mb-4">
                Final Confirmation
              </h3>
              <p className="text-gray-600 mb-4">
                You're about to delete your account with the following details:
              </p>

              <div className="bg-gray-50 border border-gray-200 rounded-md p-4 mb-4">
                {user?.name && (
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600">Name:</span>
                    <span className="font-medium text-gray-800">
                      {user.name}
                    </span>
                  </div>
                )}
                {user?.email && (
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-medium text-gray-800">
                      {user.email}
                    </span>
                  </div>
                )}
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Account Type:</span>
                  <span className="font-medium text-gray-800">
                    {user?.subscriptionPlan || "Free"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Account created:</span>
                  <span className="font-medium text-gray-800">
                    {new Date(user?.createdAt).toLocaleDateString()}
                  </span>
                </div>
                {user?.reports && user.reports.length > 0 && (
                  <div className="flex justify-between mt-2">
                    <span className="text-gray-600">Number of reports:</span>
                    <span className="font-medium text-gray-800">
                      {user.reports.length}
                    </span>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-2">
                  Type "UNSPOKENN" to confirm
                </label>
                <input
                  type="text"
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-gray-5 focus:border-gray-5"
                  value={deletionConfirm}
                  onChange={(e) => setDeletionConfirm(e.target.value)}
                  placeholder='Type "UNSPOKENN" to confirm'
                />
                <p className="mt-2 text-sm text-gray-500">
                  Please type "UNSPOKENN" (all caps) to confirm account deletion
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-between mt-8">
          {step > 1 ? (
            <button
              onClick={handleBack}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-5"
            >
              Previous
            </button>
          ) : (
            <Link
              to="/profile/settings"
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-5"
            >
              Cancel
            </Link>
          )}

          {step < 3 ? (
            <button
              onClick={handleNext}
              disabled={step === 1 && !confirmed}
              className={`px-4 py-2 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-5 ${
                step === 1 && !confirmed
                  ? "bg-gray-4 cursor-not-allowed"
                  : "bg-gray-7 hover:bg-gray-6"
              }`}
            >
              Continue
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || deletionConfirm !== "UNSPOKENN"}
              className={`px-4 py-2 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
                isSubmitting || deletionConfirm !== "UNSPOKENN"
                  ? "bg-red-400 cursor-not-allowed"
                  : "bg-red-600 hover:bg-red-700"
              }`}
            >
              {isSubmitting ? "Processing..." : "Request Account Deletion"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileDeletionRequest;
